/**
 * Frontend service pro uzivatelske nastaveni notifikaci
 */

import { apiClient } from './api';

// Typy - synchronizovane s backend models

export type NotificationChannel = 'email' | 'discord' | 'sms' | 'push';
export type AlertSeverityLevel = 'critical' | 'high' | 'medium' | 'low';

export interface ChannelPreferences {
  email: boolean;
  discord: boolean;
  sms: boolean;
  push: boolean;
}

export interface SeverityPreferences {
  critical: ChannelPreferences;
  high: ChannelPreferences;
  medium: ChannelPreferences;
  low: ChannelPreferences;
}

export interface DiscordDeviceWebhooks {
  [deviceId: string]: string;
}

export interface NotificationSettings {
  user_id: number;
  enabled: boolean;
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
  discord_enabled: boolean;
  discord_webhook_url: string | null;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  categories: {
    critical: boolean;
    warnings: boolean;
    info: boolean;
    maintenance: boolean;
  };
  severity_preferences: SeverityPreferences;
  discord_device_webhooks: DiscordDeviceWebhooks;
  discord_global_webhook: string | null;
  notifications_disabled: boolean;
  disabled_at: string | null;
  disabled_reason: string | null;
  updated_at: string;
}

export interface NotificationSettingsResponse {
  settings: NotificationSettings;
  warnings: string[];
}

export interface UpdateNotificationSettingsDTO {
  enabled?: boolean;
  email_enabled?: boolean;
  sms_enabled?: boolean;
  push_enabled?: boolean;
  discord_enabled?: boolean;
  quiet_hours_enabled?: boolean;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
  categories?: Partial<NotificationSettings['categories']>;
  severity_preferences?: Partial<SeverityPreferences>;
  discord_device_webhooks?: DiscordDeviceWebhooks;
  discord_global_webhook?: string | null;
  notifications_disabled?: boolean;
  disabled_reason?: string | null;
}

// API funkce

export async function getNotificationSettings(): Promise<NotificationSettingsResponse> {
  return apiClient.get<NotificationSettingsResponse>('/users/notification-settings');
}

export async function updateNotificationSettings(
  dto: UpdateNotificationSettingsDTO,
): Promise<NotificationSettingsResponse> {
  return apiClient.put<NotificationSettingsResponse>('/users/notification-settings', dto);
}

export async function addDeviceDiscordWebhook(
  deviceId: string,
  webhookUrl: string,
): Promise<NotificationSettingsResponse> {
  return apiClient.post<NotificationSettingsResponse>(
    '/users/notification-settings/discord/device',
    { device_id: deviceId, webhook_url: webhookUrl },
  );
}

export async function removeDeviceDiscordWebhook(
  deviceId: string,
): Promise<NotificationSettingsResponse> {
  return apiClient.delete<NotificationSettingsResponse>(
    `/users/notification-settings/discord/device/${deviceId}`,
  );
}

export async function setGlobalDiscordWebhook(
  webhookUrl: string | null,
): Promise<NotificationSettingsResponse> {
  return apiClient.put<NotificationSettingsResponse>(
    '/users/notification-settings/discord/global',
    { webhook_url: webhookUrl },
  );
}

export async function removeGlobalDiscordWebhook(): Promise<NotificationSettingsResponse> {
  return apiClient.delete<NotificationSettingsResponse>(
    '/users/notification-settings/discord/global',
  );
}

export async function testDiscordWebhook(
  webhookUrl: string,
  type: 'device' | 'global',
): Promise<{ success: boolean; message: string }> {
  return apiClient.post<{ success: boolean; message: string }>(
    '/users/notification-settings/test-webhook',
    { webhook_url: webhookUrl, type },
  );
}

// Default hodnoty
export const DEFAULT_SEVERITY_PREFERENCES: SeverityPreferences = {
  critical: { email: true, discord: true, sms: true, push: true },
  high: { email: true, discord: true, sms: false, push: true },
  medium: { email: false, discord: true, sms: false, push: true },
  low: { email: false, discord: false, sms: false, push: true },
};

// Helpers
export const SEVERITY_LABELS: Record<AlertSeverityLevel, string> = {
  critical: 'Kritické',
  high: 'Vysoké',
  medium: 'Střední',
  low: 'Nízké',
};

export const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: 'Email',
  discord: 'Discord',
  sms: 'SMS',
  push: 'Push notifikace',
};

export const SEVERITY_COLORS: Record<AlertSeverityLevel, string> = {
  critical: 'text-red-500 bg-red-100 dark:bg-red-900/30',
  high: 'text-orange-500 bg-orange-100 dark:bg-orange-900/30',
  medium: 'text-yellow-500 bg-yellow-100 dark:bg-yellow-900/30',
  low: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',
};
